import React, { useMemo, useState } from 'react'
import { Modal, Button } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import {
  FieldTypeEnum,
  SelectTypeEnum,
  type FieldConfig,
  type NumberFieldConfig,
  type SelectFieldConfig
} from '../../../../core/components/InputManager/types.ts'
import { DealFields } from '../../../../state_manager/data_models/deal_record.ts'
import { DealCommitmentEntryFields } from '../../../../state_manager/data_models/deal_commitment_entry.ts'
import {
  LOOKUP_CATEGORY_INDUSTRY,
  LOOKUP_CATEGORY_CRITICAL_TECHNOLOGY_AREA,
  LOOKUP_CATEGORY_SECURITY_TYPE
} from '../../../../state_manager/constants/lookupCategories.ts'
import {
  buildCriticalTechnologyAreaField,
  CRITICAL_TECHNOLOGY_AREA_COLUMN,
  CRITICAL_TECHNOLOGY_AREA_LABEL,
  CRITICAL_TECHNOLOGY_AREA_FULL_NAME
} from '../../../global_configs/criticalTechnologyAreas.tsx'
import { ACTION_TYPE_INSERT } from '../../../../state_manager/globals.ts'
import { TOAST_CONFIG } from '../../../../core/components/FormTable/constants.ts'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../../core/auth/authService'
import { store, type RootState } from '../../../../state_manager/store.ts'
import { updateDealRecord } from '../../../../state_manager/state_update_actions/deals/index.ts'
import { useCurrencies } from '../../../currencies/useCurrencies.ts'
import { useLookups } from '../../../global_configs/hooks/useLookups.ts'
import { useFundOptions } from '../../../global_configs/hooks/useFundOptions.ts'
import { updateDealCommitments } from '../../services/dealsService.ts'
import { patchRound } from '../../../deal_execution/services/dealExecutionService.ts'
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../../core/utils/endpoints.ts'
import type { CommitGaps } from '../../utils/commitGate.ts'

/**
 * Shown when a deal is about to move into an origination Committed stage
 * without the details the stage's transaction drafts are built from. The
 * missing ones are editable in place; "Save and commit" writes them (round
 * fields via PATCH /rounds, the commitment via the commitments endpoint) and
 * then lets the stage change proceed. "Commit anyway" proceeds without saving.
 *
 * `inline=false` (the New Deal drawer) lists the gaps only — the form itself
 * is right behind the modal, so the footer is [Back to form] [Commit anyway].
 */
export interface CommitDealModalProps {
  open: boolean
  record: Record<string, any>
  gaps: CommitGaps
  inline: boolean
  onResolve: (proceed: boolean) => void
}

const KEY_COMMIT_FUND = 'commit__fund'
const KEY_COMMIT_AMOUNT = 'commit__amount'

const positive = (value: unknown): boolean => {
  const n = typeof value === 'string' ? Number(value) : value
  return typeof n === 'number' && Number.isFinite(n) && n > 0
}

const formatMoney = (symbol: string, value: unknown): string =>
  positive(value) ? `${symbol}${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '-'

/** PATCH /accounts — the InputManager autosave route ({id, <column>: value}). */
const patchAccount = async (
  accessToken: string | null,
  updates: { id: string } & Record<string, unknown>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`
    const response = await fetch(buildFullUrl(getBackendBaseUrl(), API_ROUTES.COMPANIES.PATCH), {
      method: API_ROUTES.COMPANIES.PATCH.method,
      headers,
      body: JSON.stringify(updates)
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || body.error || `Account patch failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('patchAccount failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

export function CommitDealModal({ open, record, gaps, inline, onResolve }: CommitDealModalProps) {
  const dispatch = useDispatch()
  const { getCurrencyById } = useCurrencies()
  const { getLookupOptions } = useLookups()
  const pipelines = useSelector((state: RootState) => state.globalConfigs?.pipelines || [])
  const { options: fundOptions } = useFundOptions({ scope: 'any' })

  const dealId: string | null = record?.[DealFields.deals__id] || null
  const roundId: string | null = record?.[DealFields.rounds__id] || null
  const accountId: string | null = record?.[DealFields.deals__account] || record?.[DealFields.accounts__id] || null
  const dealName: string = record?.[DealFields.deals__name] || 'this deal'
  const currencySymbol = getCurrencyById(record?.[DealFields.deals__currency])?.symbol || '$'
  const securityTypeOptions = useMemo(() => getLookupOptions(LOOKUP_CATEGORY_SECURITY_TYPE), [getLookupOptions])
  const industryOptions = useMemo(() => getLookupOptions(LOOKUP_CATEGORY_INDUSTRY), [getLookupOptions])
  const ctaOptions = useMemo(
    () => getLookupOptions(LOOKUP_CATEGORY_CRITICAL_TECHNOLOGY_AREA),
    [getLookupOptions]
  )

  // The origination pipeline's default fund pre-fills the commitment row when
  // it is one the gate would offer; otherwise the picker opens empty.
  const defaultFundId = useMemo(() => {
    const pipeline = pipelines.find((p) => p.id === record?.[DealFields.deals__pipeline_origination])
    const candidate = pipeline?.default_fund || null
    return candidate && fundOptions.some((o) => o.value === candidate) ? candidate : null
  }, [pipelines, fundOptions, record])

  const [values, setValues] = useState<Record<string, any>>(() => ({
    [KEY_COMMIT_FUND]: defaultFundId,
    [KEY_COMMIT_AMOUNT]: null,
    [DealFields.rounds__security_type]: null,
    [DealFields.rounds__pre_money_valuation]: null,
    [DealFields.rounds__post_money_valuation]: null,
    [DealFields.accounts__industry]: null,
    [DealFields.accounts__critical_technology_area]: null
  }))
  const [saving, setSaving] = useState(false)

  const fields = useMemo((): FieldConfig[] => {
    const list: FieldConfig[] = []
    if (gaps.commitment) {
      list.push({
        key: KEY_COMMIT_FUND,
        label: 'Commitment fund',
        type: FieldTypeEnum.SELECT,
        value: values[KEY_COMMIT_FUND] || '',
        editable: true,
        required: true,
        selectType: SelectTypeEnum.FILTER,
        options: fundOptions.map((o) => ({ value: o.value, label: o.label })),
        allowClear: true
      } as SelectFieldConfig)
      list.push({
        key: KEY_COMMIT_AMOUNT,
        label: 'Commitment',
        type: FieldTypeEnum.CURRENCY,
        value: values[KEY_COMMIT_AMOUNT] ?? '',
        editable: true,
        required: true,
        currencySymbol,
        min: 0,
        step: 0.01
      } as NumberFieldConfig)
    }
    if (gaps.securityType) {
      list.push({
        key: DealFields.rounds__security_type,
        label: 'Security Type',
        type: FieldTypeEnum.SELECT,
        value: values[DealFields.rounds__security_type] || '',
        editable: true,
        required: true,
        selectType: SelectTypeEnum.LOOKUP,
        lookupCategory: LOOKUP_CATEGORY_SECURITY_TYPE,
        allowClear: true
      } as SelectFieldConfig)
    }
    if (gaps.valuation) {
      list.push({
        key: DealFields.rounds__pre_money_valuation,
        label: 'Pre-money Valuation',
        type: FieldTypeEnum.CURRENCY,
        value: values[DealFields.rounds__pre_money_valuation] ?? '',
        editable: true,
        currencySymbol,
        min: 0,
        step: 0.01
      } as NumberFieldConfig)
      list.push({
        key: DealFields.rounds__post_money_valuation,
        label: 'Post-money Valuation',
        type: FieldTypeEnum.CURRENCY,
        value: values[DealFields.rounds__post_money_valuation] ?? '',
        editable: true,
        currencySymbol,
        min: 0,
        step: 0.01
      } as NumberFieldConfig)
    }
    if (gaps.industry) {
      list.push({
        key: DealFields.accounts__industry,
        label: 'Industry',
        type: FieldTypeEnum.SELECT,
        value: values[DealFields.accounts__industry] || '',
        editable: true,
        required: true,
        selectType: SelectTypeEnum.LOOKUP,
        lookupCategory: LOOKUP_CATEGORY_INDUSTRY,
        allowClear: true
      } as SelectFieldConfig)
    }
    if (gaps.criticalTechnologyArea) {
      // Not `required`: an SBIC-fund investment may fall under no CTA, so this
      // is the one gap "Save and commit" accepts empty.
      list.push(
        buildCriticalTechnologyAreaField({
          options: ctaOptions,
          value: values[DealFields.accounts__critical_technology_area],
          key: DealFields.accounts__critical_technology_area
        })
      )
    }
    return list
  }, [gaps, values, fundOptions, currencySymbol, ctaOptions])

  const satisfied = useMemo(() => {
    const rows: Array<{ label: string; value: string }> = []
    if (!gaps.commitment) {
      rows.push({
        label: 'Commitment',
        value: formatMoney(currencySymbol, record?.[DealFields.prog__commitment_amount])
      })
    }
    if (!gaps.securityType) {
      const id = record?.[DealFields.rounds__security_type]
      rows.push({
        label: 'Security Type',
        value:
          record?.[DealFields.rounds__security_type__label] ||
          securityTypeOptions.find((o) => o.value === id)?.label ||
          '-'
      })
    }
    if (!gaps.valuation) {
      const pre = record?.[DealFields.rounds__pre_money_valuation]
      const post = record?.[DealFields.rounds__post_money_valuation]
      rows.push({
        label: positive(pre) ? 'Pre-money Valuation' : 'Post-money Valuation',
        value: formatMoney(currencySymbol, positive(pre) ? pre : post)
      })
    }
    if (!gaps.industry) {
      const id = record?.[DealFields.accounts__industry]
      rows.push({
        label: 'Industry',
        value:
          record?.[DealFields.accounts__industry__label] || industryOptions.find((o) => o.value === id)?.label || '-'
      })
    }
    // Only shown when the deal already carries one. A non-SBIC commitment
    // clears the gap without ever having asked, and a green tick for a
    // question we never put would be a lie.
    if (!gaps.criticalTechnologyArea && record?.[DealFields.accounts__critical_technology_area]) {
      const id = record[DealFields.accounts__critical_technology_area]
      rows.push({
        label: CRITICAL_TECHNOLOGY_AREA_LABEL,
        value:
          record?.[DealFields.accounts__critical_technology_area__label] ||
          ctaOptions.find((o) => o.value === id)?.label ||
          '-'
      })
    }
    return rows
  }, [gaps, record, currencySymbol, securityTypeOptions, industryOptions, ctaOptions])

  const missingLabels = [
    gaps.commitment ? 'Commitment' : null,
    gaps.securityType ? 'Security Type' : null,
    gaps.valuation ? 'Valuation (pre- or post-money)' : null,
    gaps.industry ? 'Industry' : null,
    // Spelled out here, unlike the field label: this list is plain text with
    // no field beside it, so there is no help "?" to expand the acronym.
    gaps.criticalTechnologyArea ? CRITICAL_TECHNOLOGY_AREA_FULL_NAME : null
  ].filter(Boolean) as string[]

  const saveAndCommit = async () => {
    if (!dealId) {
      showErrorToast('This deal has not been saved yet — commit anyway or go back to the form', TOAST_CONFIG)
      return
    }
    const roundUpdates: Record<string, unknown> = {}
    const reduxUpdates: Record<string, unknown> = {}

    if (gaps.securityType) {
      const securityType = values[DealFields.rounds__security_type]
      if (!securityType) {
        showErrorToast('Select a security type, or choose Commit anyway', TOAST_CONFIG)
        return
      }
      roundUpdates.security_type = securityType
      reduxUpdates[DealFields.rounds__security_type] = securityType
      const label = securityTypeOptions.find((o) => o.value === securityType)?.label
      if (label) reduxUpdates[DealFields.rounds__security_type__label] = label
    }
    if (gaps.valuation) {
      const pre = values[DealFields.rounds__pre_money_valuation]
      const post = values[DealFields.rounds__post_money_valuation]
      if (!positive(pre) && !positive(post)) {
        showErrorToast('Enter a pre- or post-money valuation, or choose Commit anyway', TOAST_CONFIG)
        return
      }
      roundUpdates.pre_money_valuation = positive(pre) ? Number(pre) : null
      roundUpdates.post_money_valuation = positive(post) ? Number(post) : null
      reduxUpdates[DealFields.rounds__pre_money_valuation] = roundUpdates.pre_money_valuation
      reduxUpdates[DealFields.rounds__post_money_valuation] = roundUpdates.post_money_valuation
    }
    const cta = values[DealFields.accounts__critical_technology_area]
    const industry = values[DealFields.accounts__industry]
    if (gaps.criticalTechnologyArea && cta && !accountId) {
      showErrorToast('This deal has no company to hold the Critical Technology Area', TOAST_CONFIG)
      return
    }
    if (gaps.industry && !industry) {
      showErrorToast('Select an industry, or choose Commit anyway', TOAST_CONFIG)
      return
    }
    if (gaps.industry && !accountId) {
      showErrorToast('This deal has no company to hold the industry', TOAST_CONFIG)
      return
    }
    const fundId = values[KEY_COMMIT_FUND]
    const amount = values[KEY_COMMIT_AMOUNT]
    if (gaps.commitment && (!fundId || !positive(amount))) {
      showErrorToast('Choose a fund and enter the commitment amount, or choose Commit anyway', TOAST_CONFIG)
      return
    }
    if (Object.keys(roundUpdates).length > 0 && !roundId) {
      showErrorToast(
        'This deal has no round to hold the security type or valuation — add one on the Detail tab',
        TOAST_CONFIG
      )
      return
    }

    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      if (Object.keys(roundUpdates).length > 0 && roundId) {
        const result = await patchRound(token, { id: roundId, ...roundUpdates })
        if (!result.success) {
          showErrorToast(result.error || 'Failed to save the round details', TOAST_CONFIG)
          return
        }
      }
      if (gaps.industry && accountId) {
        const result = await patchAccount(token, { id: accountId, industry })
        if (!result.success) {
          showErrorToast(result.error || 'Failed to save the industry', TOAST_CONFIG)
          return
        }
        reduxUpdates[DealFields.accounts__industry] = industry
        reduxUpdates[DealFields.accounts__industry__label] =
          industryOptions.find((o) => o.value === industry)?.label || null
      }
      // The CTA is optional, so this saves only when one was actually picked —
      // leaving it blank is a legitimate answer and must not fail the save.
      if (gaps.criticalTechnologyArea && cta && accountId) {
        const result = await patchAccount(token, {
          id: accountId,
          [CRITICAL_TECHNOLOGY_AREA_COLUMN]: cta
        })
        if (!result.success) {
          showErrorToast(result.error || 'Failed to save the Critical Technology Area', TOAST_CONFIG)
          return
        }
        reduxUpdates[DealFields.accounts__critical_technology_area] = cta
        reduxUpdates[DealFields.accounts__critical_technology_area__label] =
          ctaOptions.find((o) => o.value === cta)?.label || null
      }
      if (gaps.commitment) {
        const entry = {
          [DealCommitmentEntryFields.deal_commitments__id]: crypto.randomUUID(),
          [DealCommitmentEntryFields.deal_commitments__fund_id]: fundId,
          [DealCommitmentEntryFields.deal_commitments__amount]: Number(amount),
          [DealCommitmentEntryFields.prog__deal_commitments__action]: ACTION_TYPE_INSERT
        }
        const result = await updateDealCommitments(dealId, [entry], token)
        if (result.error) {
          showErrorToast(result.error, TOAST_CONFIG)
          return
        }
        const rows = Array.isArray(result.data) ? (result.data as Array<Record<string, unknown>>) : [entry]
        reduxUpdates[DealFields.prog__deal_commitments] = rows
        reduxUpdates[DealFields.prog__commitment_amount] = rows.reduce((sum, row) => {
          const value = row[DealCommitmentEntryFields.deal_commitments__amount]
          return sum + (positive(value) ? Number(value) : 0)
        }, 0)
      }
      dispatch(updateDealRecord({ dealId, updates: reduxUpdates }))
      toast.success('Deal details saved', TOAST_CONFIG)
      onResolve(true)
    } finally {
      setSaving(false)
    }
  }

  const footer = inline
    ? [
        <Button key='cancel' onClick={() => onResolve(false)}>
          Cancel
        </Button>,
        <Button key='anyway' onClick={() => onResolve(true)}>
          Commit anyway
        </Button>,
        <Button key='save' type='primary' loading={saving} onClick={saveAndCommit}>
          Save and commit
        </Button>
      ]
    : [
        <Button key='back' onClick={() => onResolve(false)}>
          Back to form
        </Button>,
        <Button key='anyway' type='primary' onClick={() => onResolve(true)}>
          Commit anyway
        </Button>
      ]

  return (
    <Modal
      title={`Complete ${dealName} before committing`}
      open={open}
      onCancel={() => onResolve(false)}
      width={520}
      footer={footer}
      data-debug='commit-deal-modal'
    >
      <p className='text-sm text-gray-500 mb-3'>
        {inline
          ? 'The committed deal drafts its transactions from these. Fill them in here, or commit anyway and add them later.'
          : 'The committed deal drafts its transactions from these. Go back to fill them in, or commit anyway and add them later.'}
      </p>
      {satisfied.length > 0 && (
        <div className='mb-3 space-y-1'>
          {satisfied.map((row) => (
            <div key={row.label} className='flex items-center text-sm'>
              <CheckCircleOutlined className='text-green-600 mr-2' />
              <span className='text-gray-500 w-40'>{row.label}</span>
              <span>{row.value}</span>
            </div>
          ))}
        </div>
      )}
      {inline ? (
        <InputManager
          inputMode='edit'
          labelPosition='above'
          fields={fields}
          onChange={(key: string, value: any) => setValues((prev) => ({ ...prev, [key]: value }))}
          className='commit-deal-input-manager'
        />
      ) : (
        <div className='space-y-1'>
          {missingLabels.map((label) => (
            <div key={label} className='flex items-center text-sm'>
              <CloseCircleOutlined className='text-red-500 mr-2' />
              <span className='text-gray-500 w-40'>{label}</span>
              <span className='text-red-500'>Missing</span>
            </div>
          ))}
        </div>
      )}
      {gaps.valuation && inline && (
        <p className='text-xs text-gray-400 mt-2 mb-0'>Either valuation satisfies the check.</p>
      )}
    </Modal>
  )
}
